import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import { Table } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import FilterDrawerShell from 'components/Form/filterDrawerShell';
import FilterSection, { FilterField } from 'components/Form/filterSection';
import AppliedFilterSummary, { summariseSelection, summariseOption } from '../../components/AppliedFilterSummary';
import useStickyIdentity from '../../components/useStickyIdentity';
import { getIssuancesApi, getUniformItemsApi, uniformErrorMessage } from 'api/services/uniformManagement';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge, issuanceStatusOptions, employeeGenderFilterOptions } from '../../constants';
import PageHeader from 'components/PageHeader';
import ListShell from 'components/ListShell';
import { useSelector } from 'react-redux';
import IssuanceFormModal from './IssuanceFormModal';
import { UNIFORM_LANE, truncatedText } from '../../components/columns';

/** The server reads `company_user`, `uniform_item`, `cost_center` and
 *  `position` as a single id OR a comma-separated list. axios' default
 *  serializer would turn an array into `company_user[]=1&company_user[]=2`,
 *  which the server does not read at all — so the ids are joined here, the
 *  same way BulkIssuance sends its scope. */
const idsCsv = (selected) => (selected || []).map((o) => o.value).join(',');

/** Identity comparison for a multi-select's draft vs applied snapshot.
 *  Compares CONTENT, not array identity: `useState` hands back a new array on
 *  every change, so `!==` would report every drawer as dirty forever and pin
 *  "Apply Filter" enabled. Order-sensitive on purpose — reordering is not
 *  reachable through the UI, and sorting on every keystroke to defend against
 *  a state that cannot occur is not worth the cost. */
const sameSelection = (a, b) => idsCsv(a) === idsCsv(b);

/** The server's per-field id cap, mirrored to warn before the request is sent.
 *  The server still enforces it and its 400 is surfaced verbatim by
 *  `uniformErrorMessage`, so this is a courtesy — but a necessary one now that
 *  every field here has a select-all: one click on a tenant with more than 200
 *  employees produces an over-cap CSV, and an opaque 400 is a poor answer to a
 *  button the UI offered. Same 200 as `ReportFilterBar` and `BulkIssuance`. */
const MAX_FILTER_IDS = 200;

const IssuanceList = () => {
  const navigate = useNavigate();
  const { userData, userDataLoading, costCenter, costCenterLoading } = useSelector((state) => state.common);
  const { uniformPositionOptions, uniformPositionsLoading } = useUniformMgmt();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [uniformItems, setUniformItems] = useState([]);
  const [uniformItemsLoading, setUniformItemsLoading] = useState(false);
  // Bumped to re-run the list fetch after an issuance is created. A token
  // rather than a direct `fetchData()` call: calling it by hand returns an
  // `ignore` cleanup that nobody holds, so that request could never be
  // invalidated and could land after a newer one. Going through the effect
  // means every request on this page — filters, paging, status, post-issue
  // refresh — is armed by the same guard.
  const [reloadToken, setReloadToken] = useState(0);

  // Employee, item, cost centre and position are multi-selects and hold an
  // ARRAY of options; gender stays a single option (a closed three-value
  // vocabulary where picking all of them means the same as picking none).
  const [filterEmployee, setFilterEmployee] = useState([]);
  const [filterItem, setFilterItem] = useState([]);
  const [filterCasa, setFilterCasa] = useState([]);
  const [filterPosition, setFilterPosition] = useState([]);
  const [filterGender, setFilterGender] = useState(null);
  // Applied snapshot - what fetchData actually searches with. Only Apply
  // commits the draft filters into these, so picking a value in the drawer
  // no longer fires an API call by itself. That split matters more now than it
  // did: a multi-select fires an onChange per tick, so a live fetch would mean
  // one request per checkbox.
  const [appliedFilterEmployee, setAppliedFilterEmployee] = useState([]);
  const [appliedFilterItem, setAppliedFilterItem] = useState([]);
  const [appliedFilterCasa, setAppliedFilterCasa] = useState([]);
  const [appliedFilterPosition, setAppliedFilterPosition] = useState([]);
  const [appliedFilterGender, setAppliedFilterGender] = useState(null);
  const isFilterDirty = !sameSelection(filterEmployee, appliedFilterEmployee)
    || !sameSelection(filterItem, appliedFilterItem)
    || !sameSelection(filterCasa, appliedFilterCasa)
    || !sameSelection(filterPosition, appliedFilterPosition)
    || (filterGender?.value ?? null) !== (appliedFilterGender?.value ?? null);

  /** How many of the five drawer fields hold a value. Counts FIELDS, not
   *  values: ticking a second cost centre narrows nothing extra about how
   *  filtered the list is, and a badge reading "14" for one busy picker would
   *  say less than "1". */
  const countSet = (employee, item, casa, position, gender) =>
    [employee.length > 0, item.length > 0, casa.length > 0, position.length > 0, Boolean(gender)]
      .filter(Boolean).length;
  const draftFilterCount = countSet(filterEmployee, filterItem, filterCasa, filterPosition, filterGender);

  /** Which of the four multis the DRAFT has pushed past the server's cap, named
   *  individually — a message blaming all four sends the user to narrow three
   *  pickers that were never the problem. Measured on the draft because the cap
   *  has to be reported while the user is ticking, and Apply is what it gates;
   *  an over-cap selection therefore never reaches the applied state or a
   *  request. */
  const overCapFields = [
    filterEmployee.length > MAX_FILTER_IDS && 'Employee',
    filterCasa.length > MAX_FILTER_IDS && 'Cost Center',
    filterPosition.length > MAX_FILTER_IDS && 'Position',
    filterItem.length > MAX_FILTER_IDS && 'Uniform Item',
  ].filter(Boolean);
  const filterIdsExceeded = overCapFields.length > 0;
  /** What the trigger badge and the summary line describe: the query the table
   *  is actually showing, which is the only honest answer to "why is this list
   *  short" while the drawer is closed. */
  const appliedFilterCount = countSet(
    appliedFilterEmployee, appliedFilterItem, appliedFilterCasa, appliedFilterPosition, appliedFilterGender
  );
  const appliedSummaryEntries = [
    summariseSelection('Employee', appliedFilterEmployee),
    summariseSelection('Cost Center', appliedFilterCasa),
    summariseSelection('Position', appliedFilterPosition),
    summariseSelection('Uniform Item', appliedFilterItem),
    summariseOption('Gender', appliedFilterGender),
  ];

  const clearDraftFilters = () => {
    setFilterEmployee([]);
    setFilterItem([]);
    setFilterCasa([]);
    setFilterPosition([]);
    setFilterGender(null);
  };

  /** Commits the draft into the applied snapshot — the only thing that fires a
   *  request — and returns to page 1, since page 4 of the old result set
   *  rarely exists in the new one. */
  const applyFilters = () => {
    setAppliedFilterEmployee(filterEmployee);
    setAppliedFilterItem(filterItem);
    setAppliedFilterCasa(filterCasa);
    setAppliedFilterPosition(filterPosition);
    setAppliedFilterGender(filterGender);
    setPageSettings(prev => ({ ...prev, page: 1 }));
    setShowFilter(false);
  };

  const employeeOptions = (userData || [])
    .slice()
    .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' }))
    .map(u => ({
      label: `${u.first_name} ${u.last_name}${u.emp_no ? ` (${u.emp_no})` : ''}`,
      value: u.id, emp_no: u.emp_no || ''
    }));
  const casaOptions = (costCenter || []).map(c => ({ label: c.name || c.code, value: c.id, code: c.code || '' }));
  // Positions a uniform is mapped to, not every position in the company:
  // the rest return an empty grid when picked. Masters keeps the full list.
  const positionOptions = uniformPositionOptions;

  useEffect(() => {
    setUniformItemsLoading(true);
    getUniformItemsApi({ page_size: 1000 }).then(response => {
      setUniformItems(
        (response.data?.results || [])
          .map(i => ({ label: i.name, value: i.id }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      );
    }).catch(e => {
      toast.error(uniformErrorMessage(e, 'Failed to load uniform items'), { position: "top-right", autoClose: 3000, transition: Bounce });
    }).finally(() => setUniformItemsLoading(false));
  }, []);

  // Guarded with `ignore` so a slow response for a filter combination the user
  // has since changed can't clobber newer results — including the loading-flag
  // reset, which is how a stale response used to leave the table showing the
  // previous scope's rows with no spinner to say more was coming. The same
  // "latest request wins" shape the reports tabs and BulkIssuance already use.
  const fetchData = useCallback(() => {
    let ignore = false;
    setLoading(true);
    const params = { page: pageSettings.page, page_size: pageSettings.pageSize };
    if (statusFilter) params.status = statusFilter;
    // `.length &&` rather than truthiness: an empty array is truthy, and
    // sending `company_user=` would be a filter the user cannot see or clear.
    if (appliedFilterEmployee.length) params.company_user = idsCsv(appliedFilterEmployee);
    if (appliedFilterItem.length) params.uniform_item = idsCsv(appliedFilterItem);
    if (appliedFilterCasa.length) params.cost_center = idsCsv(appliedFilterCasa);
    if (appliedFilterPosition.length) params.position = idsCsv(appliedFilterPosition);
    if (appliedFilterGender) params.gender = appliedFilterGender.value;
    getIssuancesApi(params).then(response => {
      if (ignore) return;
      setData(response.data?.results || []);
      setPageSettings(prev => ({ ...prev, total: response.data?.count || 0 }));
    }).catch(e => {
      if (ignore) return;
      toast.error(uniformErrorMessage(e, 'Failed to load issuances'), { position: "top-right", autoClose: 3000, transition: Bounce });
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [pageSettings.page, pageSettings.pageSize, statusFilter, appliedFilterEmployee, appliedFilterItem, appliedFilterCasa, appliedFilterPosition, appliedFilterGender]);

  // Returns fetchData's cleanup so the guard is actually armed on unmount and
  // on every re-run; `() => { fetchData(); }` would drop it on the floor.
  //
  // `reloadToken` is a dependency of the EFFECT, not of `fetchData` — the
  // callback never reads it, so putting it there is an unused dep the linter
  // rightly rejects. Here it does the one job it has: bumping it re-runs the
  // effect, which re-fetches through the same `ignore` guard as every other
  // trigger.
  useEffect(() => fetchData(), [fetchData, reloadToken]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  // Page 1 alone is not enough to guarantee a refetch — if the list is already
  // on page 1 that setter is a no-op and nothing re-runs — so the token is
  // bumped too. Both land in the same render, so this is one request, not two.
  const handleIssued = () => {
    setShowCreateModal(false);
    setPageSettings(prev => ({ ...prev, page: 1 }));
    setReloadToken(t => t + 1);
  };

  const pinLeft = useStickyIdentity();

  const columns = [
    {
      // Reference and Employee are pinned: twelve columns scroll well past the
      // window, and without them every cell to the right is anonymous.
      title: 'Reference', dataIndex: 'batch_reference', ...pinLeft(170),
      render: (ref, record) => ref ? (
        record.batch ? (
          <button type="button" className="table-link"
            onClick={() => navigate(`/uniform-management/issuance/batches/${record.batch}`)}>
            {ref}
          </button>
        ) : ref
      ) : '-'
    },
    {
      title: 'Employee', dataIndex: 'company_user_name', ...pinLeft(200, { secondary: true }),
      render: (text, record) => (
        <div>
          <button type="button" className="table-link"
            onClick={() => navigate(`/uniform-management/issuance/${record.id}`)}>{text}</button>
          <br /><small className="text-muted">{record.company_user_emp_no}</small>
        </div>
      )
    },
    { title: 'Cost Center', dataIndex: 'casa_name', render: (val) => val || '-' },
    { title: 'Uniform Item', dataIndex: 'uniform_item_name', ...truncatedText(UNIFORM_LANE) },
    {
      title: 'Size', dataIndex: 'size_detail',
      render: (detail) => detail ? `${detail.name} (${detail.code})` : '-'
    },
    { title: 'Qty', dataIndex: 'quantity' },
    { title: 'Warehouse', dataIndex: 'warehouse_name' },
    { title: 'Issued Date', dataIndex: 'issuance_date', render: (d) => formatDateHandler(d) },
    {
      // When the row was RECORDED, which is not the issued date: that one is
      // picked by the operator and can be backdated. This is also what the
      // list is ordered by (newest first), so the column explains the order.
      title: 'Created', dataIndex: 'created_at', width: 110,
      render: (d) => (d ? formatDateHandler(d) : '-'),
    },
    { title: 'Issued By', dataIndex: 'issued_by_name' },
    {
      title: 'Next Replacement', dataIndex: 'next_replacement_date',
      render: (d) => d ? formatDateHandler(d) : '-'
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (status) => <span style={getStatusBadge(status)}>{status}</span>
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Button label="View" className="btn btn-sm btn-outline-primary"
          onClick={() => navigate(`/uniform-management/issuance/${record.id}`)} />
      )
    }
  ];

  const filterItems = [
    { key: '', label: 'All' },
    ...issuanceStatusOptions.map(o => ({ key: o.value, label: o.label }))
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title="Uniform Issuance"
          actions={
            <>
              {/* A raw <button>, not the shared `Button`, only because that
                  one renders `{icon}{label}` with no slot for a trailing
                  badge. Classes and size are identical to its siblings.

                  The badge counts the APPLIED filters, not the draft: it is
                  the only thing on screen that answers "is this list filtered"
                  while the drawer is shut, and a draft the user picked and
                  never applied has not filtered anything. (Inside the drawer
                  the title badge counts the draft instead — there the question
                  is about the controls, and the same split ReportFilterBar
                  documents applies.) */}
              <button type="button" className="btn btn-outline-primary btn-sm"
                aria-haspopup="dialog" aria-expanded={showFilter}
                onClick={() => setShowFilter(true)}>
                <i className="fa fa-filter" style={{ marginRight: 6 }} aria-hidden="true" />
                Filters
                {appliedFilterCount > 0 && (
                  <>
                    {/* Borrowed from the shared filter-drawer stylesheet
                        (loaded via FilterSection below) so the trigger's badge
                        and the drawer's are the same object to the eye. */}
                    <span className="filter-section__count ms-2" aria-hidden="true">
                      {appliedFilterCount}
                    </span>
                    {/* The badge is decorative; the count belongs in the
                        button's accessible name so it is announced with the
                        control rather than as a stray number after it. */}
                    <span className="visually-hidden">
                      , {appliedFilterCount} {appliedFilterCount === 1 ? 'filter' : 'filters'} applied
                    </span>
                  </>
                )}
              </button>
              <Button label="Bulk Issuance" icon="fa fa-users" className="btn btn-outline-primary btn-sm"
                onClick={() => navigate('/uniform-management/bulk-issuance')} />
              <Button label="New Issuance" icon="fa fa-plus" className="btn btn-primary btn-sm"
                onClick={() => setShowCreateModal(true)} />
            </>
          }
        />

        {/* Every filter on this page lives behind a closed drawer, so without
            this a filtered table is just a short table. Same component and
            same wording contract as the reports bar's summary. */}
        <AppliedFilterSummary lead="Filtered by:" divider={false} entries={appliedSummaryEntries} />

        <ListShell
          filters={filterItems}
          activeKey={statusFilter}
          onFilterChange={(key) => { setStatusFilter(key); setPageSettings(prev => ({ ...prev, page: 1 })); }}
          resultCount={pageSettings.total}
          loading={loading}
        >
          <Table columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
            pagination={{
              total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
              pageSizeOptions, itemRender, showSizeChanger: true
            }}
            rowClassName="clickable-row"
            onRow={(record) => ({
              onClick: (e) => {
                if (e.target.closest('a, button')) return;
                navigate(`/uniform-management/issuance/${record.id}`);
              },
            })}
            onChange={handleChange} scroll={{ x: "max-content" }} />
        </ListShell>

        {/* Built on the app's shared drawer primitives rather than the
            hand-rolled antd <Drawer> this page used to carry, and NOT on
            `ReportFilterBar`: that is a horizontal always-visible bar for a
            report page, and dropping it in here would delete the "Filters"
            trigger and change this list's layout language for no gain. The
            shell, by contrast, is a genuine drop-in — seven other list pages
            (assets, salary, leave, expenses, resumes, resignations,
            documents) already use it, and adopting it here gets the count
            badge in the title, the sectioned body and the pinned footer for
            free while making Uniform Management look like the rest of the
            app instead of like itself.

            Esc closes it: antd's Drawer has `keyboard` on by default and the
            shell keeps `closable`, so there is both an X and an Esc route
            out. Nothing here overrides either.

            The shell's `activeCount` is the DRAFT count — inside the drawer
            the question is "what have I got set in these controls". The
            trigger badge up in the header counts the applied filters
            instead; see the note there. */}
        <FilterDrawerShell
          open={showFilter}
          onClose={() => setShowFilter(false)}
          activeCount={draftFilterCount}
          onClearAll={clearDraftFilters}
          onApply={applyFilters}
          applyDisabled={!isFilterDirty || filterIdsExceeded}
        >
          {/* Grouped who / where / what rather than a flat list of five.
              The section names match `filterDrawer.jsx`'s (Employee,
              Organisation) so the two drawers read as the same product:
              position and cost centre are both "which part of the business",
              gender is a property of the person. */}
          <FilterSection title="Employee" count={countSet(filterEmployee, [], [], [], filterGender)}>
            {/* `onChange={(next) => set(next || [])}`, never the setter
                directly: react-select hands back `null` (not `[]`) when a
                multi is emptied through its clear button, and a null here
                would break every `.length` read on it. */}
            <FilterField label="Employee" htmlFor="issuance-filter-employee">
              <FilterSelect inputId="issuance-filter-employee"
                isMulti checkboxMenu selectAll maxVisibleValues={3} isClearable
                onChange={(next) => setFilterEmployee(next || [])} optionLabel="label" optionValue="value"
                options={employeeOptions} value={filterEmployee} placeholder="Filter by Employee"
                isLoading={userDataLoading} noOptionsLabel="employees" />
            </FilterField>
            <FilterField label="Gender" htmlFor="issuance-filter-gender">
              <FilterSelect inputId="issuance-filter-gender" isClearable
                onChange={setFilterGender} optionLabel="label" optionValue="value"
                options={employeeGenderFilterOptions} value={filterGender}
                placeholder="Filter by Gender" isLoading={false} />
            </FilterField>
          </FilterSection>

          <FilterSection title="Organisation" count={countSet([], [], filterCasa, filterPosition, null)}>
            <FilterField label="Cost Center" htmlFor="issuance-filter-cost-center">
              <FilterSelect inputId="issuance-filter-cost-center"
                isMulti checkboxMenu selectAll maxVisibleValues={3} isClearable
                onChange={(next) => setFilterCasa(next || [])} optionLabel="label" optionValue="value"
                options={casaOptions} value={filterCasa} placeholder="Filter by Cost Center"
                isLoading={costCenterLoading} noOptionsLabel="cost centers" />
            </FilterField>
            <FilterField label="Position" htmlFor="issuance-filter-position">
              <FilterSelect inputId="issuance-filter-position"
                isMulti checkboxMenu selectAll maxVisibleValues={3} isClearable
                onChange={(next) => setFilterPosition(next || [])} optionLabel="label" optionValue="value"
                options={positionOptions} value={filterPosition} placeholder="Filter by Position"
                isLoading={uniformPositionsLoading} noOptionsLabel="positions" />
            </FilterField>
          </FilterSection>

          <FilterSection title="Uniform" count={countSet([], filterItem, [], [], null)}>
            <FilterField label="Uniform Item" htmlFor="issuance-filter-item">
              <FilterSelect inputId="issuance-filter-item"
                isMulti checkboxMenu selectAll maxVisibleValues={3} isClearable
                onChange={(next) => setFilterItem(next || [])} optionLabel="label" optionValue="value"
                options={uniformItems} value={filterItem} placeholder="Filter by Item"
                isLoading={uniformItemsLoading} noOptionsLabel="uniform items" />
            </FilterField>
          </FilterSection>

          {/* Said in the drawer, beside the pickers that caused it and above
              the footer whose Apply it disables — not left to come back as an
              opaque 400 after the user clicks. Necessary because every field
              here offers select-all, so one click can breach the cap. */}
          {filterIdsExceeded && (
            <div className="text-danger" style={{ fontSize: 12, marginTop: 12 }}>
              {/* The visible text carries the live counts; it is hidden from
                  assistive tech because it changes on every further tick, and
                  a live region would re-announce each time. */}
              <span aria-hidden="true">
                Too many values in {overCapFields.join(' and ')} — at most {MAX_FILTER_IDS} each.
              </span>
              {/* Announced instead: one sentence with no changing part, so
                  `role="status"` (polite) fires once when the cap is first
                  crossed and stays silent while ticking continues past it. */}
              <span className="visually-hidden" role="status">
                Too many values selected in {overCapFields.join(' and ')}.
                At most {MAX_FILTER_IDS} each. Apply is unavailable until you remove some.
              </span>
            </div>
          )}
        </FilterDrawerShell>

        <IssuanceFormModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onIssued={handleIssued}
        />
      </div>
    </div>
  );
};

export default IssuanceList;
